import { useState, useEffect, useRef } from 'react';
import type { HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import { Spinner } from '../styles/components';
import { generateInvestmentsCSV, downloadCSV, generateExportFilename } from '../utils/csvExport';
import { fetchAnnouncementsExport } from '../utils/api';
import { PHYSICAL_TYPES } from '../utils/colors';
import type { FilterState } from '../types';
import { useT } from '../i18n';

interface ExportDialogProps {
  /** Physical-scoped, date-effective filters the projects table is showing. */
  filters: FilterState;
  /** Map/chart aggregate selection (country, Wahlkreis, bubble cluster). */
  clusterIds: number[] | null;
  /** Industry drill narrowing (AND-joined, like the table fetch). */
  industriesNarrow?: string[];
  totalEntryCount: number;
  onClose: () => void;
}

const Overlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Portaled to document.body. Unlike the widget's inline content (capped at
     <= 9 so it scrolls under the host site's nav), this is an explicit modal
     dialog with a full-viewport backdrop and must cover everything, including
     the host nav. Sit at the top of the stack, one above AnnouncementModal. */
  z-index: 2147483647;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease;
`;

const DialogContainer = styled.div<{ isVisible: boolean }>`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: 0 12px 32px rgba(24, 28, 68, 0.18), 0 4px 8px rgba(0, 0, 0, 0.06);
  min-width: 380px;
  max-width: 90vw;
  transform: ${props => props.isVisible ? 'scale(1)' : 'scale(0.95)'};
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border};
`;

// Modal dialog title — sans 16px semibold, matching the budget widget's
// export dialog heading style.
const DialogTitle = styled.h3`
  font-family: var(--sans-stack);
  font-size: ${scaled(16)};
  font-weight: ${theme.fontWeight.semibold};
  color: var(--color-text-default);
  margin: 0;
`;

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 16px;
  border: none;
  background: transparent;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.15s ease;

  &:hover {
    background: rgba(24, 28, 68, 0.08);
    color: ${theme.colors.text.primary};
  }
`;

const DialogContent = styled.div`
  padding: ${theme.spacing.lg};
`;

const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${theme.colors.primary};
    background: rgba(24, 28, 68, 0.02);
  }

  input:checked + & {
    border-color: ${theme.colors.primary};
    background: rgba(24, 28, 68, 0.04);
  }
`;

const RadioInput = styled.input`
  margin-top: 2px;
  accent-color: ${theme.colors.primary};
  cursor: pointer;
`;

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OptionTitle = styled.span`
  font-family: ${theme.fontFamilySans};
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;

const OptionDescription = styled.span`
  font-family: ${theme.fontFamilySans};
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.text.secondary};
`;

const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.border};
`;

// Shared `.button.button--{primary|outline}.button--size-m`. Variant maps:
//   primary   → button--primary (active green pill)
//   secondary → button--outline (transparent w/ border)
// `:disabled` styling is per-instance — shared button has no disabled rules.
const ButtonBase = styled.div`
  &[aria-disabled='true'] { opacity: 0.6; cursor: not-allowed; }
`;
interface DialogButtonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'primary' | 'secondary';
}
const Button = ({ variant = 'secondary', className, ...rest }: DialogButtonProps) => (
  <ButtonBase
    {...rest}
    className={`button button--${variant === 'primary' ? 'primary' : 'outline'} button--size-m ${className ?? ''}`.trim()}
  />
);

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.xl};
`;

const LoadingText = styled.span`
  font-family: ${theme.fontFamilySans};
  font-size: ${theme.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;

type ExportOption = 'current' | 'all';

export function ExportDialog({
  filters,
  clusterIds,
  industriesNarrow,
  totalEntryCount,
  onClose,
}: ExportDialogProps) {
  const m = useT();
  const [isVisible, setIsVisible] = useState(false);
  const [selectedOption, setSelectedOption] = useState<ExportOption>('current');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isLoading]);

  // Close on overlay click
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleExport = async () => {
    setIsLoading(true);
    setLoadingProgress(m.exportDialog.loading);

    try {
      // "Alle Daten" still scopes to investment projects — the export covers
      // the physical tier only, never the financial transactions.
      const rows = selectedOption === 'current'
        ? await fetchAnnouncementsExport(filters, clusterIds ?? undefined, industriesNarrow)
        : await fetchAnnouncementsExport({
            dateFrom: null,
            dateTo: null,
            federalStates: [],
            investmentTypes: [...PHYSICAL_TYPES],
            investmentStatuses: [],
            industries: [],
            companies: [],
            search: '',
            spotlight: '',
          });

      setLoadingProgress(m.exportDialog.building);
      const csvContent = generateInvestmentsCSV(rows);
      const filename = generateExportFilename();

      downloadCSV(csvContent, filename);
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      setLoadingProgress(m.exportDialog.failed);
      setIsLoading(false);
    }
  };

  const dialogContent = (
    <Overlay className="color-data" isVisible={isVisible} onClick={handleOverlayClick}>
      <DialogContainer ref={containerRef} isVisible={isVisible}>
        <DialogHeader>
          <DialogTitle>{m.exportDialog.title}</DialogTitle>
          <CloseButton
            onClick={() => { if (!isLoading) onClose(); }}
            aria-disabled={isLoading}
            title={m.common.close}
          >
            <span className="icon" data-icon="close" />
          </CloseButton>
        </DialogHeader>

        {isLoading ? (
          <LoadingContainer>
            <Spinner />
            <LoadingText>{loadingProgress}</LoadingText>
          </LoadingContainer>
        ) : (
          <>
            <DialogContent>
              <OptionGroup>
                <OptionLabel>
                  <RadioInput
                    type="radio"
                    name="exportOption"
                    checked={selectedOption === 'current'}
                    onChange={() => setSelectedOption('current')}
                  />
                  <OptionContent>
                    <OptionTitle>{m.exportDialog.currentView}</OptionTitle>
                    <OptionDescription>
                      {m.exportDialog.currentViewDescription(totalEntryCount)}
                    </OptionDescription>
                  </OptionContent>
                </OptionLabel>

                <OptionLabel>
                  <RadioInput
                    type="radio"
                    name="exportOption"
                    checked={selectedOption === 'all'}
                    onChange={() => setSelectedOption('all')}
                  />
                  <OptionContent>
                    <OptionTitle>{m.exportDialog.allData}</OptionTitle>
                    <OptionDescription>
                      {m.exportDialog.allDataDescription}
                    </OptionDescription>
                  </OptionContent>
                </OptionLabel>
              </OptionGroup>
            </DialogContent>

            <DialogFooter>
              <Button variant="secondary" onClick={onClose}>
                {m.common.cancel}
              </Button>
              <Button variant="primary" onClick={handleExport} data-icon-before="download">
                {m.exportDialog.export}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContainer>
    </Overlay>
  );

  return createPortal(dialogContent, document.body);
}
